import {
  type CreateWikiChangeSetRequest,
  CSRF_HEADER,
  type FolderId,
  type ProjectDetail,
  WIKI_CHANGE_SET_TEXT_MAX,
  type WikiChangeSet,
  type WikiPageType,
  type WikiPageView,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Markdown } from "@/components/markdown/Markdown";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { canChange } from "../projects/capabilities.ts";
import { FolderField } from "../projects/FolderPicker.tsx";
import { changeSetPath, navigate, wikiPath, wikiTabPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { ChangeSetPublishDialog } from "./ChangeSetPublishDialog.tsx";
import { useWikiPage, wikiKeys } from "./queries.ts";
import { useReviewGuard } from "./use-review-guard.tsx";

const PAGE_TYPES: readonly WikiPageType[] = ["summary", "concept", "entity", "other"];

/**
 * The page editor (wiki-editor-and-cleanup D3): a new page in `folderId`, or a proposed change
 * to the shown version of `pageId`. It writes a one-item change set; Save for review opens its
 * review, Publish saves first and then opens the change-set publish confirmation. Nothing
 * changes under Pages until the change set is published. Items already in a change set are
 * edited in the review's inline editor.
 */
export function PageEditor(props: {
  project: ProjectDetail;
  pageId: string | null;
  folderId: string | null;
}) {
  const { project, pageId } = props;
  const { t } = useTranslation();
  const writable = canChange(project, "wiki.draft");
  const page = useWikiPage(project.id, pageId ?? "", undefined, writable && pageId !== null);
  if (!writable) {
    return (
      <Notice>{t(project.frozenAt ? "wiki.pageEditor.frozen" : "wiki.pageEditor.noAccess")}</Notice>
    );
  }
  if (pageId) {
    if (page.isError) {
      return <Notice tone="danger">{page.error.message}</Notice>;
    }
    if (!page.data) {
      return <Skeleton className="h-80 w-full" />;
    }
    return <Editor key={pageId} project={project} page={page.data} folderId={null} />;
  }
  return <Editor project={project} folderId={props.folderId} />;
}

function Editor(props: {
  project: ProjectDetail;
  page?: WikiPageView | undefined;
  folderId: string | null;
}) {
  const { project, page } = props;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  // What the author edits against, fixed when the editor opens: a refetch that brings a newer
  // version neither changes the base (the item rebases or goes stale in review, ADR 0015) nor
  // makes untouched fields look changed.
  const [initial] = useState(() => ({
    title: page?.title ?? "",
    pageType: page?.pageType ?? ("other" as WikiPageType),
    markdown: page?.markdown ?? "",
    baseVersion: page?.shownVersion ?? 0,
  }));
  const [title, setTitle] = useState(initial.title);
  const [pageType, setPageType] = useState<WikiPageType>(initial.pageType);
  const [markdown, setMarkdown] = useState(initial.markdown);
  const [rationale, setRationale] = useState("");
  const [folderId, setFolderId] = useState<FolderId | null>(props.folderId as FolderId | null);
  // Saved: the guard lets go first, then the editor moves on (to the review or the dialog).
  const [saved, setSaved] = useState<{ set: WikiChangeSet; then: "review" | "publish" } | null>(
    null,
  );
  const dirty =
    !saved &&
    (title !== initial.title ||
      pageType !== initial.pageType ||
      markdown !== initial.markdown ||
      rationale !== "");
  const guard = useReviewGuard(dirty, `${title}\n\n${markdown}`);
  const review = (set: WikiChangeSet) =>
    navigate(changeSetPath(project.id, set.id, set.items[0]?.id), { replace: true });
  useEffect(() => {
    if (saved?.then === "review") {
      review(saved.set);
    }
  });
  const save = useMutation({
    mutationFn: async (_then: "review" | "publish"): Promise<WikiChangeSet> => {
      const json: CreateWikiChangeSetRequest = {
        title,
        rationale,
        items: [
          page
            ? {
                op: "edit",
                pageId: page.id,
                // Against the version opened: a newer one rebases or goes stale in review.
                baseVersion: initial.baseVersion,
                title,
                markdown,
                pageType,
              }
            : { op: "create", title, markdown, pageType, folderId },
        ],
      };
      return ok(
        await api.api.projects[":projectId"].wiki["change-sets"].$post(
          { param: { projectId: project.id }, json },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      );
    },
    onSuccess: (set, then) => {
      void queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
      setSaved({ set, then });
    },
  });
  const submit = (then: "review" | "publish") => {
    if (title.trim() && !save.isPending && !saved) {
      save.mutate(then);
    }
  };
  const cancel = () =>
    guard.confirm(() =>
      navigate(
        page
          ? wikiPath(project.id, page.id)
          : props.folderId
            ? `${wikiTabPath(project.id, "pages")}?folder=${encodeURIComponent(props.folderId)}`
            : wikiTabPath(project.id, "pages"),
      ),
    );
  const mayPublish = canChange(project, "wiki.publish");
  const busy = save.isPending || saved !== null;
  // The dialogs sit outside the form: a portal still bubbles React events, so a submit inside
  // them would also submit the editor.
  return (
    <>
      <form
        data-esi-content="wiki"
        // A focused field scrolls clear of the sticky action bar (WCAG 2.4.11).
        className="flex flex-col gap-6 [&_:is(input,select,textarea,button)]:scroll-mb-24"
        aria-labelledby={`${id}-heading`}
        onSubmit={(e) => {
          e.preventDefault();
          submit("review");
        }}
      >
        <header className="flex flex-col gap-1.5">
          <h2 id={`${id}-heading`} className="text-xl font-semibold tracking-tight text-fg">
            {page
              ? t("wiki.pageEditor.change", { title: initial.title })
              : t("wiki.pageEditor.new")}
          </h2>
          <p className="max-w-prose text-sm text-fg-secondary">
            {page
              ? t("wiki.pageEditor.changeHint", { version: initial.baseVersion })
              : t("wiki.pageEditor.newHint")}
          </p>
        </header>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-title`}>{t("wiki.editor.title")}</Label>
              <Input
                id={`${id}-title`}
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-type`}>{t("wiki.editor.pageType")}</Label>
              <NativeSelect
                id={`${id}-type`}
                value={pageType}
                className="sm:w-56"
                onChange={(e) => setPageType(e.currentTarget.value as WikiPageType)}
              >
                {PAGE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`wiki.pageTypes.${type}`)}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {page ? null : (
              <FolderField
                projectId={project.id}
                section="wiki"
                value={folderId}
                onChange={setFolderId}
                canCreate={canChange(project, "folder.manage")}
              />
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-md`}>{t("wiki.editor.markdown")}</Label>
              <Textarea
                id={`${id}-md`}
                value={markdown}
                maxLength={200000}
                onChange={(e) => setMarkdown(e.currentTarget.value)}
                className="min-h-80 font-mono text-[13px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-why`}>{t("wiki.pageEditor.why")}</Label>
              <Textarea
                id={`${id}-why`}
                rows={3}
                value={rationale}
                maxLength={WIKI_CHANGE_SET_TEXT_MAX}
                aria-describedby={`${id}-why-hint`}
                onChange={(e) => setRationale(e.currentTarget.value)}
              />
              <p id={`${id}-why-hint`} className="text-xs text-fg-muted">
                {t("wiki.pageEditor.whyHint")}
              </p>
            </div>
          </div>
          <section aria-labelledby={`${id}-preview`} className="flex min-w-0 flex-col gap-1.5">
            <h3 id={`${id}-preview`} className="text-sm font-medium text-fg">
              {t("wiki.editor.preview")}
            </h3>
            <div className="min-h-80 rounded-lg border border-line bg-surface-raised p-5">
              {markdown.trim() ? (
                <Markdown markdown={markdown} />
              ) : (
                <p className="text-sm text-fg-muted">{t("wiki.pageEditor.previewEmpty")}</p>
              )}
            </div>
          </section>
        </div>
        {save.isError ? <Notice tone="danger">{save.error.message}</Notice> : null}
        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-line bg-surface py-3">
          {mayPublish ? (
            <Button type="button" disabled={busy} onClick={() => submit("publish")}>
              {t("wiki.pageEditor.publish")}
            </Button>
          ) : null}
          <Button type="submit" variant={mayPublish ? "outline" : "default"} disabled={busy}>
            {save.isPending ? t("wiki.editor.saving") : t("wiki.pageEditor.save")}
          </Button>
          <Button type="button" variant="ghost" onClick={cancel}>
            {t("wiki.editor.cancel")}
          </Button>
        </div>
      </form>
      {saved?.then === "publish" ? (
        <ChangeSetPublishDialog
          open
          // Saved already: closing without publishing leaves the change set in review.
          onClose={() => review(saved.set)}
          project={project}
          changeSet={saved.set}
        />
      ) : null}
      {guard.dialog}
    </>
  );
}
